<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import ClusterRow from './ClusterRow.vue';
import CreateClusterModal from './CreateClusterModal.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const searchQuery = ref('');
const selectedTypeId = ref('');
const createModalRef = ref(null);

const clusters = computed(() => store.getters['clusters/getClusters']);
const uiFlags = computed(() => store.getters['clusters/getUIFlags']);
const clusterTypes = computed(() => store.getters['clusterTypes/getTypes']);

const filteredClusters = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return clusters.value;
  return clusters.value.filter(c =>
    [c.name, c.cluster_type?.label_singular].some(v =>
      (v || '').toLowerCase().includes(q)
    )
  );
});

const isEmpty = computed(
  () => !uiFlags.value.isFetching && !filteredClusters.value.length
);

const loadClusters = () =>
  store.dispatch('clusters/fetch', {
    q: searchQuery.value.trim() || undefined,
    clusterTypeId: selectedTypeId.value || undefined,
  });

watch(selectedTypeId, loadClusters);

const openCluster = cluster =>
  router.push({ name: 'cluster_details', params: { clusterId: cluster.id } });

const onCreated = cluster => openCluster(cluster);

onMounted(async () => {
  await store.dispatch('clusterTypes/fetch');
  loadClusters();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Toolbar -->
    <header
      class="flex items-center justify-between gap-3 px-6 pt-5 pb-3 border-b shrink-0 border-n-weak"
    >
      <h1 class="flex items-center gap-2 text-xl font-semibold text-n-slate-12">
        {{ t('CLUSTERS.HEADER_TITLE') }}
        <span class="text-base font-normal text-n-slate-10">
          {{ clusters.length }}
        </span>
      </h1>
      <div class="flex items-center gap-2">
        <!-- Cluster type filter -->
        <select
          v-model="selectedTypeId"
          class="h-8 px-2.5 text-sm border rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand"
        >
          <option value="">{{ t('CLUSTERS.FILTER.ALL_TYPES') }}</option>
          <option
            v-for="ct in clusterTypes"
            :key="ct.id"
            :value="ct.id"
          >
            {{ ct.label_plural }}
          </option>
        </select>

        <div class="relative w-52 max-w-full">
          <span
            class="absolute -translate-y-1/2 i-lucide-search left-2.5 top-1/2 text-n-slate-10 size-4"
          />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('CLUSTERS.SEARCH_PLACEHOLDER')"
            class="w-full h-8 pl-8 pr-3 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
          />
        </div>
        <Button
          :label="t('CLUSTERS.ACTIONS.NEW')"
          icon="i-lucide-plus"
          size="sm"
          color="blue"
          @click="createModalRef?.open()"
        />
      </div>
    </header>

    <!-- Body -->
    <section class="flex-1 min-h-0 overflow-y-auto">
      <!-- Loading skeleton -->
      <div
        v-if="uiFlags.isFetching && !clusters.length"
        class="divide-y divide-n-weak/60"
      >
        <div
          v-for="n in 6"
          :key="n"
          class="flex items-center gap-3 px-6 py-3 animate-pulse"
        >
          <span class="rounded-xl size-9 bg-n-alpha-2 shrink-0" />
          <div class="flex flex-col gap-1.5">
            <span class="h-3 rounded w-36 bg-n-alpha-2" />
            <span class="h-2.5 rounded w-24 bg-n-alpha-1" />
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="isEmpty"
        class="flex flex-col items-center justify-center h-full gap-4 px-6 text-center"
      >
        <span
          class="flex items-center justify-center rounded-2xl size-14 bg-n-alpha-1 text-n-slate-10"
        >
          <span class="i-lucide-layers size-7" />
        </span>
        <div class="flex flex-col gap-1">
          <p class="text-base font-medium text-n-slate-12">
            {{
              searchQuery ? t('CLUSTERS.NO_RESULTS') : t('CLUSTERS.EMPTY_TITLE')
            }}
          </p>
          <p class="max-w-sm text-sm text-n-slate-10">
            {{
              searchQuery ? t('CLUSTERS.NO_RESULTS_HINT') : t('CLUSTERS.EMPTY')
            }}
          </p>
        </div>
        <Button
          v-if="!searchQuery"
          :label="t('CLUSTERS.ACTIONS.NEW')"
          icon="i-lucide-plus"
          size="sm"
          color="blue"
          @click="createModalRef?.open()"
        />
      </div>

      <!-- List -->
      <div v-else class="divide-y divide-n-weak/60">
        <ClusterRow
          v-for="cluster in filteredClusters"
          :key="cluster.id"
          :cluster="cluster"
          @select="openCluster"
        />
      </div>
    </section>

    <CreateClusterModal ref="createModalRef" @created="onCreated" />
  </div>
</template>
